<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'
import IconFacebook from '@/design/icons/IconFacebook.vue'
import IconInstagram from '@/design/icons/IconInstagram.vue'
import IconDiscord from '@/design/icons/IconDiscord.vue'
import IconSteam from '@/design/icons/IconSteam.vue'

const currentYear = new Date().getFullYear()

// Payment logos are fully dynamic now: GET /api/payments/footer returns the
// combined list of gateway footer logos + standalone footer logos, deduped.
// Shape: { logos: [{ name, logo }] }
const payLogos = ref([])

// Legal/policy links are admin-managed (Static Pages). We render ONLY the pages
// that exist + are active, using their admin-set titles — nothing hardcoded.
// Shape from GET /api/pages: [{ id, title, slug }]
const legalPages = ref([])

// Footer copy + socials + company info come from /api/settings — admin-controlled.
const settings = ref({
  companyName:  'Evoluxu',
  regNumber:    '',
  address:      '',
  city:         '',
  supportEmail: '',
  facebookLink: '',
  instagramLink: '',
  facebookLogo:  '',
  instagramLogo: '',
  discordLink:  '',
  steamLink:    '',
  copyright: '',
})

// Social links: only show the icon if the admin has set a value. No silent fallback to a default
// URL so admin panel state matches what the visitor sees on the site.
const facebookHref  = computed(() => settings.value.facebookLink  || null)
const instagramHref = computed(() => settings.value.instagramLink || null)
const discordHref   = computed(() => settings.value.discordLink   || null)
const steamHref     = computed(() => settings.value.steamLink     || null)
// Uploaded social logos (data URLs from admin). When set, they render instead of the
// built-in icon — and they show even before a link is added (link comes later).
const facebookLogo  = computed(() => settings.value.facebookLogo  || null)
const instagramLogo = computed(() => settings.value.instagramLogo || null)
const supportEmail  = computed(() => settings.value.supportEmail || '')
const copyrightText = computed(() => settings.value.copyright || `© ${currentYear} EVOLUXU · ALL RIGHTS RESERVED`)

onMounted(async () => {
  try {
    const { data } = await api.get(EP.paymentsFooter)
    if (Array.isArray(data?.logos)) payLogos.value = data.logos
  } catch {}

  try {
    const { data } = await api.get('/api/settings')
    if (data && typeof data === 'object') {
      settings.value = { ...settings.value, ...data }
    }
  } catch {}

  try {
    const { data } = await api.get(EP.pages)
    if (Array.isArray(data)) legalPages.value = data
  } catch {}
})
</script>

<template>
  <footer class="ftr">
    <div class="ftr__divider"></div>

    <div class="ftr__cols">
      <div class="ftr__col ftr__brand">
        <RouterLink to="/" class="ftr__brand-link" aria-label="Evoluxu home">
          <span class="ftr__brand-plate">EVOLUXU</span>
        </RouterLink>
        <p class="ftr__brand-tag">Premium ARK: Survival Ascended community server.</p>
        <div v-if="settings.companyName || settings.regNumber || settings.address || settings.city" class="ftr__company">
          <p v-if="settings.companyName" class="ftr__company-name">{{ settings.companyName }}</p>
          <p v-if="settings.regNumber" class="ftr__company-detail">Reg. No: {{ settings.regNumber }}</p>
          <p v-if="settings.address" class="ftr__company-detail">{{ settings.address }}</p>
          <p v-if="settings.city" class="ftr__company-detail">{{ settings.city }}</p>
        </div>
        <p v-if="supportEmail" class="ftr__brand-support">
          <a :href="`mailto:${supportEmail}`" class="ftr__support-link">{{ supportEmail }}</a>
        </p>
      </div>
      <div class="ftr__links">
        <div class="ftr__col">
          <h4>Server</h4>
          <ul>
            <li><RouterLink to="/">Home</RouterLink></li>
            <li><RouterLink to="/market">Store</RouterLink></li>
          </ul>
        </div>
        <div class="ftr__col">
          <h4>Account</h4>
          <ul>
            <li><RouterLink to="/profile">Profile</RouterLink></li>
            <li><RouterLink to="/cart">Cart</RouterLink></li>
            <li><RouterLink to="/register">Sign in</RouterLink></li>
          </ul>
        </div>
        <div v-if="legalPages.length" class="ftr__col">
          <h4>Legal</h4>
          <ul>
            <li v-for="p in legalPages" :key="p.slug">
              <RouterLink :to="`/legal/${p.slug}`">{{ p.title }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="ftr__divider"></div>

    <div class="ftr__bottom">
      <ul v-if="payLogos.length" class="ftr__pay" aria-label="Accepted payment methods">
        <li class="ftr__pay-group">
          <span v-for="(p, i) in payLogos" :key="`${p.logo}-${i}`" class="ftr__pay-item">
            <img :src="p.logo" :alt="p.name || 'Payment method'"/>
          </span>
        </li>
      </ul>

      <div class="ftr__social">
        <!-- Facebook — uploaded logo (if any) overrides the built-in icon; shows even
             without a link yet (renders as a non-clickable mark until a link is set). -->
        <template v-if="facebookHref || facebookLogo">
          <a v-if="facebookHref" :href="facebookHref" target="_blank" rel="noopener" aria-label="Facebook" class="ftr__social-link">
            <img v-if="facebookLogo" :src="facebookLogo" alt="Facebook" class="ftr__social-logo"/>
            <IconFacebook v-else class="ftr__social-icon"/>
          </a>
          <span v-else class="ftr__social-link" aria-label="Facebook">
            <img v-if="facebookLogo" :src="facebookLogo" alt="Facebook" class="ftr__social-logo"/>
            <IconFacebook v-else class="ftr__social-icon"/>
          </span>
        </template>

        <!-- Instagram — same logo/icon + link-optional behavior -->
        <template v-if="instagramHref || instagramLogo">
          <a v-if="instagramHref" :href="instagramHref" target="_blank" rel="noopener" aria-label="Instagram" class="ftr__social-link">
            <img v-if="instagramLogo" :src="instagramLogo" alt="Instagram" class="ftr__social-logo"/>
            <IconInstagram v-else class="ftr__social-icon"/>
          </a>
          <span v-else class="ftr__social-link" aria-label="Instagram">
            <img v-if="instagramLogo" :src="instagramLogo" alt="Instagram" class="ftr__social-logo"/>
            <IconInstagram v-else class="ftr__social-icon"/>
          </span>
        </template>

        <a v-if="discordHref" :href="discordHref" target="_blank" rel="noopener" aria-label="Discord" class="ftr__social-link">
          <IconDiscord class="ftr__social-icon"/>
        </a>
        <a v-if="steamHref" :href="steamHref" target="_blank" rel="noopener" aria-label="Steam" class="ftr__social-link">
          <IconSteam class="ftr__social-icon"/>
        </a>
      </div>

      <div class="ftr__copy">{{ copyrightText }}</div>
    </div>
  </footer>
</template>

<style scoped>
.ftr {
  background: linear-gradient(
    180deg,
    rgba(11, 10, 8, 0.62) 0%,
    rgba(11, 10, 8, 0.90) 280px,
    rgba(11, 10, 8, 0.96) 100%
  );
  padding: var(--sp-24) var(--sp-6) var(--sp-6);
  position: relative;
  margin-top: 0;
}
/* Long lead-in fade ABOVE the footer — smooths the panorama → black band */
.ftr::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 420px;
  background: linear-gradient(
    180deg,
    rgba(11, 10, 8, 0.00)  0%,
    rgba(11, 10, 8, 0.10) 30%,
    rgba(11, 10, 8, 0.32) 60%,
    rgba(11, 10, 8, 0.50) 85%,
    rgba(11, 10, 8, 0.62) 100%
  );
  pointer-events: none;
}

.ftr__divider {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, var(--gold) 30%, var(--gold) 70%, transparent 100%);
  opacity: 0.45;
  margin: 0 auto;
  max-width: var(--container-wide);
}

.ftr__cols {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--sp-12);
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: var(--sp-12) 0;
}
.ftr__col { flex: 0 0 auto; }
/* Anchor Legal to the right edge */
.ftr__col:last-child { text-align: right; }
.ftr__brand { max-width: 320px; }

/* Desktop: wrapper is invisible — its 3 cols flow as direct flex children of .ftr__cols */
.ftr__links { display: contents; }

@media (max-width: 720px) {
  .ftr__cols {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--sp-8);
    padding: var(--sp-8) 0;
  }
  /* Brand block — centered */
  .ftr__brand {
    max-width: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  .ftr__brand-tag { margin: var(--sp-3) auto 0; text-align: center; }

  /* Links wrapper — centered as a unit, 3 cols spread inside */
  .ftr__links {
    margin: 0 auto;
    width: fit-content;
    max-width: 100%;
    display: flex;
    justify-content: center;
    gap: var(--sp-8);
  }
  .ftr__links .ftr__col,
  .ftr__links .ftr__col:last-child {
    text-align: left;
  }
  .ftr__col h4 { margin-bottom: var(--sp-2); font-size: var(--fs-12); }
  .ftr__col a { font-size: var(--fs-12); }
}

/* Brand block — gold stamp plate matching the header */
.ftr__company {
  margin-top: var(--sp-3);
}
.ftr__company-name {
  margin: 0;
  font-size: var(--fs-13);
  color: var(--ink);
  font-weight: 600;
}
.ftr__company-detail {
  margin: 2px 0 0;
  font-size: var(--fs-12);
  color: var(--ink-mute);
}
.ftr__brand-support {
  margin-top: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--ink-mute);
}
.ftr__support-link {
  color: var(--gold);
  text-decoration: none;
  border-bottom: 1px dotted rgba(229, 199, 107, 0.4);
}
.ftr__support-link:hover {
  border-bottom-color: var(--gold);
}
.ftr__brand-link { display: inline-flex; align-items: center; }
.ftr__brand-plate {
  display: inline-block;
  height: 44px;
  line-height: 44px;
  padding: 0 18px;
  font-family: var(--font-display);
  font-size: 26px;
  letter-spacing: 0.22em;
  text-indent: 0.22em;
  text-align: center;
  color: var(--ink-inverse);
  border-radius: var(--r-xs);
  border: 1px solid #8C6E2A;
  background: linear-gradient(180deg, #F5DC97 0%, #E5C76B 55%, #C19F4F 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    inset 0 -1px 0 rgba(0, 0, 0, 0.18),
    0 2px 8px rgba(0, 0, 0, 0.45);
}
.ftr__brand p {
  margin: var(--sp-4) 0 0;
  color: var(--ink-soft);
  font-size: var(--fs-14);
  max-width: 30ch;
}
.ftr__col h4 {
  font-family: var(--font-display);
  font-size: var(--fs-14);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  margin: 0 0 var(--sp-4);
  color: var(--gold);
}
.ftr__col ul {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: var(--sp-2);
}
.ftr__col a {
  color: var(--ink-soft);
  font-size: var(--fs-14);
  transition: color var(--t-fast) var(--ease-road);
}
.ftr__col a:hover { color: var(--ink); }

.ftr__bottom {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--sp-6);
  max-width: var(--container-wide);
  margin: 0 auto;
  padding-top: var(--sp-6);
}
@media (max-width: 720px) {
  .ftr__bottom {
    grid-template-columns: 1fr;
    text-align: center;
    gap: var(--sp-4);
    justify-items: center;
  }
}

.ftr__pay {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;
}
/* All logos live in ONE non-wrapping group so they stay together on a single
   line and wrap as a unit (the group itself can drop to the next row), rather
   than individual logos splitting across rows. */
.ftr__pay-group {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: var(--sp-3);
  align-items: center;
}
.ftr__pay-item { display: inline-flex; align-items: center; }
.ftr__pay img {
  height: 24px;
  filter: grayscale(60%) brightness(0.85);
  opacity: 0.85;
  transition: opacity var(--t-fast) var(--ease-road), filter var(--t-fast) var(--ease-road);
}
.ftr__pay img:hover { filter: grayscale(0%) brightness(1); opacity: 1; }

.ftr__social {
  display: flex;
  gap: var(--sp-4);
  justify-content: center;
  color: var(--ink-soft);
}
.ftr__social a:hover { color: var(--gold); }
.ftr__social-link { display: inline-flex; align-items: center; color: inherit; }
.ftr__social-icon { width: 20px; height: 20px; }
.ftr__social-logo { height: 22px; width: auto; max-width: 96px; object-fit: contain; display: block; }

.ftr__copy {
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.14em;
  color: var(--ink-mute);
  text-align: right;
}
@media (max-width: 720px) { .ftr__copy { text-align: center; } }
</style>
